import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container, Stack, Group, Title, Text, Badge, Drawer, Tabs, Card, Grid } from '@mantine/core';
import { IconChartBar, IconClipboardList, IconTarget, IconRadar, IconShield } from '@tabler/icons-react';
import SectionAssessmentCardV2 from './SectionAssessmentCardV2';
import ControlEvaluationWizard from '../riskFlow/ControlEvaluationWizard';
import RiskFlowWrapperV2 from '../riskFlow/RiskFlowWrapperV2';
import ChartsSummaryV2 from './ChartsSummaryV2';
import ActionPlan from '../ActionPlan';
import RiskMonitors from '../RiskMonitors';
import SecurityTabPanel from './SecurityTabPanel';

function useInitialRef(value) {
  const ref = useRef(value);
  // lock the initial value; do not update on changes
  return ref;
}

export default function AssessmentCanvasV2({ modelId, assessmentUuid }) {
  console.log('[AssessmentCanvasV2] Received props:', { modelId, assessmentUuid });

  const risksState = useSelector((state) => state.risks.models[modelId] || {});
  const securityState = useSelector((state) => state.security.models[modelId] || {});
  const riskFormData = useSelector((state) => state.risks.models[modelId]?.riskFormData || {});
  const residualRiskData = useSelector((state) => state.residualRisk.byModel[modelId] || {});
  const savedControls = useSelector((state) => state.controls.savedControls || {});

  const originalRiskFormDataRef = useInitialRef(riskFormData);

  // Get model name from either risks or security state
  const modelName = risksState?.modelName || securityState?.modelName || 'Unknown Model';

  console.log('[AssessmentCanvasV2] Redux lookup:', {
    modelId,
    hasRisksState: !!risksState && Object.keys(risksState).length > 0,
    hasRiskFormData: !!riskFormData && Object.keys(riskFormData).length > 0,
    modelName,
    risksStateKeys: Object.keys(risksState)
  });

  const sections = useMemo(() => Object.keys(riskFormData || {}), [riskFormData]);

  // Enhanced state for card expansion - only one can be expanded at a time
  const [expandedSection, setExpandedSection] = useState(null);

  // Drawer for evaluating actions on a specific risk
  const [evalDrawer, setEvalDrawer] = useState({ opened: false, riskArea: null, source: null });
  
  // Drawer for editing risk sources and scenarios
  const [editDrawer, setEditDrawer] = useState({ opened: false, riskArea: null });

  const handleOpenEvaluation = ({ riskArea, sourceName }) => {
    const sourceObj = (riskFormData?.[riskArea]?.riskSources || []).find((s) => s?.name === sourceName) || { name: sourceName };
    setEvalDrawer({ opened: true, riskArea, source: sourceObj });
  };

  const handleCloseEvaluation = () => setEvalDrawer({ opened: false, riskArea: null, source: null });

  const handleOpenEditCard = (riskArea) => {
    setEditDrawer({ opened: true, riskArea });
  };

  const handleCloseEditCard = () => setEditDrawer({ opened: false, riskArea: null });

  const handleExpandSection = (sectionName) => {
    setExpandedSection(current => current === sectionName ? null : sectionName);
  };

  // Enhanced stats calculation
  const assessmentStats = useMemo(() => {
    const stats = {
      totalRisks: 0,
      criticalRisks: 0,
      highRisks: 0,
      totalActions: 0,
      evaluatedActions: 0,
      completedSections: 0
    };

    Object.values(riskFormData).forEach((section) => {
      let sectionHasRisks = false;

      Object.values(section.sectionRisks || {}).forEach((risks) => {
        if (Array.isArray(risks)) {
          risks.forEach(risk => {
            // Only count saved risks
            if (risk.status === 'Saved') {
              sectionHasRisks = true;
              stats.totalRisks++;
              if (risk.priority === 'Critical') stats.criticalRisks++;
              if (risk.priority === 'High') stats.highRisks++;
            }
          });
        }
      });

      if (sectionHasRisks) {
        stats.completedSections++;
      }
    });

    return stats;
  }, [riskFormData]);

  return (
    <Container fluid px="xl" py="xl">
      <Stack gap="lg">
        {/* Enhanced Header */}
        <Stack gap="xs">
          <Group gap="md" align="center">
            <IconClipboardList size={28} color="#1976d2" />
            <Title order={2} fw={700}>{modelName}</Title>
            <Text size="lg" c="dimmed" fw={500}>Assessment Canvas</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Safety risks and scenarios overview • {sections.length} risk areas analyzed
          </Text>
        </Stack>

        <Group gap="md" justify="center">
          <Stack gap={4} align="center">
            <Badge variant="filled" color="red" size="lg" radius="md">
              {assessmentStats.criticalRisks} Critical
            </Badge>
            <Text size="xs" c="dimmed">High Priority</Text>
          </Stack>
          
          <Stack gap={4} align="center">
            <Badge variant="filled" color="orange" size="lg" radius="md">
              {assessmentStats.highRisks} High
            </Badge>
            <Text size="xs" c="dimmed">Attention Needed</Text>
          </Stack>
          
          <Stack gap={4} align="center">
            <Badge variant="filled" color="indigo" size="lg" radius="md">
              {assessmentStats.totalRisks} Total
            </Badge>
            <Text size="xs" c="dimmed">All Risks</Text>
          </Stack>
        </Group>

        <Tabs defaultValue="overview" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview & Analytics
            </Tabs.Tab>
            <Tabs.Tab value="cards" leftSection={<IconClipboardList size={16} />}>
              Risk Assessment Cards
            </Tabs.Tab>
            <Tabs.Tab value="security" leftSection={<IconShield size={16} />}>
              Security Threats
            </Tabs.Tab>
            <Tabs.Tab value="actionPlan" leftSection={<IconTarget size={16} />}>
              Action Plan
            </Tabs.Tab>
            <Tabs.Tab value="analyzers" leftSection={<IconRadar size={16} />}>
              Monitors & Analyzers
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="lg">
            <ChartsSummaryV2 
              riskFormData={riskFormData} 
              residualRiskData={residualRiskData}
              savedControls={savedControls}
              modelId={modelId}
            />
          </Tabs.Panel>

          <Tabs.Panel value="security" pt="lg">
            <SecurityTabPanel modelId={modelId} />
          </Tabs.Panel>

          <Tabs.Panel value="cards" pt="lg">
            <Stack gap="md">
              {/* Instructions for better UX */}
              <Card withBorder radius="md" p="md" bg="blue.0">
                <Text size="sm" c="blue.7">
                  💡 <strong>Tip:</strong> Click on any risk area card to expand and see detailed risks and actions.
                  Cards will expand to full width for better readability.
                </Text>
              </Card>

              {/* Dynamic Grid Layout */}
              {expandedSection ? (
                // When a section is expanded, show it full width with others minimized
                <Stack gap="md">
                  {/* Minimized other sections */}
                  <Grid gutter="md">
                    {sections
                      .filter(sectionName => sectionName !== expandedSection)
                      .map((sectionName) => (
                        <Grid.Col span={6} key={`minimized-${sectionName}`}>
                          <SectionAssessmentCardV2
                            modelId={modelId}
                            assessmentUuid={assessmentUuid}
                            sectionName={sectionName}
                            sectionData={riskFormData[sectionName]}
                            originalSectionData={originalRiskFormDataRef.current?.[sectionName]}
                            residualRiskData={residualRiskData}
                            onEvaluateAction={handleOpenEvaluation}
                            onEditCard={handleOpenEditCard}
                            isExpanded={false}
                            onToggleExpand={handleExpandSection}
                            isMinimized={true}
                          />
                        </Grid.Col>
                      ))}
                  </Grid>

                  {/* Expanded section - full width */}
                  <SectionAssessmentCardV2
                    modelId={modelId}
                    assessmentUuid={assessmentUuid}
                    sectionName={expandedSection}
                    sectionData={riskFormData[expandedSection]}
                    originalSectionData={originalRiskFormDataRef.current?.[expandedSection]}
                    residualRiskData={residualRiskData}
                    onEvaluateAction={handleOpenEvaluation}
                    onEditCard={handleOpenEditCard}
                    isExpanded={true}
                    onToggleExpand={handleExpandSection}
                    isMinimized={false}
                  />
                </Stack>
              ) : (
                // Default 2-column layout when no section is expanded
                <Grid gutter="lg">
                  {sections.map((sectionName) => (
                    <Grid.Col span={{ base: 12, md: 6 }} key={sectionName}>
                      <SectionAssessmentCardV2
                        modelId={modelId}
                        assessmentUuid={assessmentUuid}
                        sectionName={sectionName}
                        sectionData={riskFormData[sectionName]}
                        originalSectionData={originalRiskFormDataRef.current?.[sectionName]}
                        residualRiskData={residualRiskData}
                        onEvaluateAction={handleOpenEvaluation}
                        onEditCard={handleOpenEditCard}
                        isExpanded={false}
                        onToggleExpand={handleExpandSection}
                        isMinimized={false}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="actionPlan" pt="lg">
            <Card withBorder radius="md" p="lg">
              <ActionPlan modelId={modelId} assessmentUuid={assessmentUuid} riskFormData={riskFormData} />
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="analyzers" pt="lg">
            <Card withBorder radius="md" p="lg">
              <RiskMonitors modelId={modelId} />
            </Card>
          </Tabs.Panel>
        </Tabs>

        <Drawer
          opened={evalDrawer.opened}
          onClose={handleCloseEvaluation}
          position="right"
          size="xl"
          title="Evaluate Actions"
        >
          {evalDrawer.opened && (
            <ControlEvaluationWizard
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              riskArea={evalDrawer.riskArea}
              source={evalDrawer.source}
            />
          )}
        </Drawer>

        <Drawer
          opened={editDrawer.opened}
          onClose={handleCloseEditCard}
          position="right"
          size="xl"
          title={`Edit Risk Sources & Scenarios - ${editDrawer.riskArea ? editDrawer.riskArea.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, l => l.toUpperCase()) : ''}`}
        >
          {editDrawer.opened && (
            <RiskFlowWrapperV2
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              riskArea={editDrawer.riskArea}
              onAreaDone={handleCloseEditCard}
              narratives={{}}
            />
          )}
        </Drawer>
      </Stack>
    </Container>
  );
}